import { withRouter } from 'react-router-dom';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { selectCompanies } from '../../Companies/selectors';
import { createStructuredSelector } from 'reselect';
import PropTypes from 'prop-types';
import { CompanySelection } from '../../Companies/components/CompanySelection';
import { Button } from '../../../components/Buttons';
import { BaseComponent } from '../../../components/BaseComponent';
import { EditSquare } from '../../../components/Icon';
import React from 'react';
import styled from 'styled-components';
import { HBox, VBox } from '../../../components/Box';
import { FormField } from '../../../components/FormFields';
import { SearchSelect } from '../../../components/SearchSelectComponent';
import { INVOICE_DIRECTIONS } from '../../Invoices/constants';
import _ from 'lodash';
import { fetchProductCompanyFinData, updateProductCompanyFinData } from '../actions';
import { selectProductCompanyFinData } from '../selectors';
import { selectWorks } from '../../Api/selectors';
import { REQUEST_PRODUCT_COMPANY_FIN_DATA, REQUEST_UPDATE_PRODUCT_COMPANY_FIN_DATA } from '../constants';
import { Loader } from '../../../components/Loader';
import ModalComponent from '../../../containers/App/Components/Modal/Modal';
import ButtonComponent from '../../../containers/App/Components/Button/Button';

import styles from './CompanySpecificField.module.css';

const STEPS = {
  VAT_FORM: 'VAT_FORM',
  CLIENT_FORM: 'CLIENT_FORM',
};

const STATE = {
  edit: false,
  selectedCompany: null,
  step: STEPS.CLIENT_FORM,
};

const Container = styled.div`
  width: 100%;
  .title {
    white-space: nowrap;
  }
`;

const ColTitle = styled.h2`
  font-size: 14px;
  text-transform: uppercase;
  font-weight: 500;
`;

/* eslint-disable react/prefer-stateless-function */
class CompanySpecificFieldComp extends BaseComponent {
  constructor(props) {
    super(props, { ...STATE });
  }

  componentDidMount() {}

  reset() {
    this.setState({
      ...STATE,
      company: null,
      purchasePrice: null,
      purchasePriceExclVat: null,
      salesPrice: null,
      salesPriceExclVat: null,
      vatIn: null,
      vatOut: null,
    });
  }

  resetFinState() {
    this.setState({
      ...STATE,
      purchasePrice: null,
      purchasePriceExclVat: null,
      salesPrice: null,
      salesPriceExclVat: null,
      vatIn: null,
      vatOut: null,
    });
  }

  get productId() {
    return this.props.product?.id;
  }

  get companyId() {
    return this.state.company?.id;
  }

  get dto() {
    return {
      purchasePrice: this.getFieldValue('purchasePrice'),
      purchasePriceExclVat: this.getFieldValue('purchasePriceExclVat'),
      salesPrice: this.getFieldValue('salesPrice'),
      salesPriceExclVat: this.getFieldValue('salesPriceExclVat'),
      vatIn: this.getFieldValue('vatIn'),
      vatOut: this.getFieldValue('vatOut'),
    };
  }

  applySelection() {
    this.setState({ company: this.state.selectedCompany, step: STEPS.VAT_FORM }, () => {
      const productId = this.productId;
      const companyId = this.companyId;
      this.props.fetchProductCompanyFinData(productId, companyId);
    });
  }

  updateVatForCompany() {
    this.props.updateProductCompanyFinData(this.productId, this.companyId, this.dto, () => {
      this.resetFinState();
    });
  }

  onNumberInputChange(evt) {
    evt.persist();
    this.updateStateWithCallback(evt.target.name, +evt.target.value, () => {
      if (['purchasePrice', 'purchasePriceExclVat'].includes(evt.target.name)) {
        this.calculateProductPrices(INVOICE_DIRECTIONS.INCOMING, evt.target.name === 'purchasePriceExclVat');
      }

      if (['salesPriceExclVat', 'salesPrice'].includes(evt.target.name)) {
        this.calculateProductPrices(INVOICE_DIRECTIONS.OUTGOING, evt.target.name === 'salesPriceExclVat');
      }
    });
  }

  updateStateWithCallback(prop, value, callback = null) {
    if (_.isObject(prop) && value === undefined) {
      this.setState({ ...(this.state || {}), ...prop }, callback);
      return this;
    }

    let state = this.state || {};
    this.setState({ ...state, [prop]: value }, callback);
    return this;
  }

  getFieldValue(field) {
    if (!!this.state[field]) return this.state[field];
    if (!!this.props.finData?.[field]) return this.props.finData[field];

    return this.props.product[field];
  }

  getVatCodeLabel(direction) {
    const value = this.props.vatCodes.find(vatCode => vatCode?.code === this.getFieldValue(direction));

    return `${value?.code} : ${value?.description}`;
  }

  get vatCodeOptions() {
    return this.props.vatCodes.map(vatCode => {
      return { value: vatCode.code, label: `${vatCode.code} : ${vatCode.description}` };
    });
  }

  setVatIn = e => {
    const { value } = e;
    this.setState(
      {
        vatIn: value,
      },
      () => {
        this.calculateProductPrices(INVOICE_DIRECTIONS.INCOMING);
      },
    );
  };

  setVatOut = e => {
    const { value } = e;
    this.setState(
      {
        vatOut: value,
      },
      () => {
        this.calculateProductPrices(INVOICE_DIRECTIONS.OUTGOING);
      },
    );
  };

  calculateProductPrices(direction, calcFromExcl = true) {
    const exclPriceKey = direction === INVOICE_DIRECTIONS.INCOMING ? 'purchasePriceExclVat' : 'salesPriceExclVat';
    const inclPriceKey = direction === INVOICE_DIRECTIONS.INCOMING ? 'purchasePrice' : 'salesPrice';
    const vatKey = direction === INVOICE_DIRECTIONS.INCOMING ? 'vatIn' : 'vatOut';

    let exclPrice = this.getFieldValue(exclPriceKey) ?? 0;
    let inclPrice = this.getFieldValue(inclPriceKey) ?? 0;
    let vatCode = this.getFieldValue(vatKey) ?? null;
    let vatObj = this.props.vatCodes.find(v => v.code === vatCode) ?? { percentage: 0 };

    if (calcFromExcl) {
      inclPrice = _.round(exclPrice * (1 + vatObj.percentage), 2);
    }

    if (!calcFromExcl) {
      exclPrice = _.round(inclPrice / (1 + vatObj.percentage), 2);
    }

    this.updateState({
      [inclPriceKey]: inclPrice,
      [exclPriceKey]: exclPrice,
    });
  }

  render() {
    return (
      <Container>
        {this.props.company ? this.props.company.fullname : ''}
        <ButtonComponent style={'ButtonSoftEditFullWidth'} onClick={this.setStateFn({ edit: true })}>
          <EditSquare />
        </ButtonComponent>
        {this.renderEdit()}
      </Container>
    );
  }

  renderEdit() {
    if (!this.state.edit) return false;
    return (
      <ModalComponent onClose={this.reset.bind(this)} title={this.modalTitleLbl}>
        {this.state.selectedCompany ? (
          <>{this.state.selectedCompany.name}</>
        ) : (
          <CompanySelection company={this.state.company} onSelect={selectedCompany => this.setState({ selectedCompany })} />
        )}
        {this.state.company && this.renderVatForm()}
        <div className="d-flex gap-2 mt-2 justify-content-end">
          {this.renderConfirmBtn()}
          <ButtonComponent style={'ButtonSoftDelete'} onClick={this.reset.bind(this)}>
            {this.lbl('cancel')}
          </ButtonComponent>
        </div>
      </ModalComponent>
    );
  }

  get modalTitleLbl() {
    if (this.state.step === STEPS.CLIENT_FORM) {
      return this.lbl('product.create.companyModalTitleClient');
    }

    if (this.state.step === STEPS.VAT_FORM) {
      return this.lbl('product.create.companyModalTitleVat');
    }

    return '';
  }

  renderConfirmBtn() {
    if (this.state.step === STEPS.CLIENT_FORM) {
      return (
        <ButtonComponent
          style={'ButtonSoftEdit'}
          onClick={this.applySelection.bind(this)}
          primary
          disabled={!this.state.selectedCompany}
        >
          {this.lbl('product.create.companyModalConfirmBtnClient')}
        </ButtonComponent>
      );
    }

    if (this.state.step === STEPS.VAT_FORM) {
      return (
        <ButtonComponent style={'ButtonSoftEdit'} onClick={this.updateVatForCompany.bind(this)}>
          {this.lbl('product.create.companyModalConfirmBtnVat')}
        </ButtonComponent>
      );
    }

    return '';
  }

  renderVatForm() {
    if (this.props.finDataWork) return <Loader small />;

    return (
      <HBox gap="16px" style={{ marginTop: '24px' }}>
        <VBox left>
          <ColTitle>{this.lbl('product.create.col.buyier')}</ColTitle>

          <div className={styles.inputContainer}>
            <label className="form-label">{this.lbl('product.create.purchase_price_excl_vat')}</label>
            <input
              className="form-control"
              name={'purchasePriceExclVat'}
              value={this.getFieldValue('purchasePriceExclVat')}
              type={'number'}
              onChange={this.onNumberInputChange.bind(this)}
              disabled={true}
            />
          </div>

          <FormField label={this.lbl('product.create.vat_in')}>
            <SearchSelect
              minWidth={'100%'}
              value={this.getVatCodeLabel('vatIn') && { label: this.getVatCodeLabel('vatIn') }}
              options={this.vatCodeOptions}
              onChange={this.setVatIn}
            />
          </FormField>

          <div className={styles.inputContainer}>
            <label className="form-label">{this.lbl('product.create.purchase_price')}</label>
            <input
              className="form-control"
              name={'purchasePrice'}
              value={this.getFieldValue('purchasePrice')}
              type={'number'}
              onChange={this.onNumberInputChange.bind(this)}
              disabled={true}
            />
          </div>
        </VBox>

        <VBox left>
          <ColTitle>{this.lbl('product.create.col.seller')}</ColTitle>

          <div className={styles.inputContainer}>
            <label className="form-label">{this.lbl('product.create.sales_price_excl_vat')}</label>
            <input
              className="form-control"
              name={'salesPriceExclVat'}
              value={this.getFieldValue('salesPriceExclVat')}
              type={'number'}
              onChange={this.onNumberInputChange.bind(this)}
              disabled={true}
            />
          </div>

          <FormField label={this.lbl('product.create.vat_out')}>
            <SearchSelect
              minWidth={'100%'}
              options={this.vatCodeOptions}
              value={this.getVatCodeLabel('vatOut') && { label: this.getVatCodeLabel('vatOut') }}
              onChange={this.setVatOut}
            />
          </FormField>

          <div className={styles.inputContainer}>
            <label className="form-label">{this.lbl('product.create.sales_price')}</label>
            <input
              className="form-control"
              name={'salesPrice'}
              value={this.getFieldValue('salesPrice')}
              type={'number'}
              onChange={this.onNumberInputChange.bind(this)}
              disabled={true}
            />
          </div>
        </VBox>
      </HBox>
    );
  }
}

CompanySpecificFieldComp.propTypes = {
  dispatch: PropTypes.func.isRequired,
};

const mapStateToProps = (state, ownProps) =>
  createStructuredSelector({
    companies: selectCompanies(),
    finDataWork: selectWorks([REQUEST_PRODUCT_COMPANY_FIN_DATA, REQUEST_UPDATE_PRODUCT_COMPANY_FIN_DATA]),
    finData: selectProductCompanyFinData(),
  });

function mapDispatchToProps(dispatch, ownProps) {
  return {
    dispatch,
    fetchProductCompanyFinData(productId, companyId) {
      dispatch(fetchProductCompanyFinData(productId, companyId));
    },
    updateProductCompanyFinData(productId, companyId, dto) {
      dispatch(updateProductCompanyFinData(productId, companyId, dto));
    },
  };
}

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export const CompanySpecificField = compose(withConnect, withRouter)(CompanySpecificFieldComp);
